import type { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { SettingsOutlined } from '@mui/icons-material';

import account from '@/assets/icons/vn-icon-account.svg';
import accountActivity from '@/assets/icons/vn-icon-account-activity.svg';
import adTag from '@/assets/icons/vn-icon-ad-tag-management.svg';
import admin from '@/assets/icons/vn-icon-admin.svg';
import adminLegacy from '@/assets/icons/vn-icon-admin-legacy.svg';
import advancedAdmin from '@/assets/icons/vn-icon-advanced-admin.svg';
import analytics from '@/assets/icons/vn-icon-analytics.svg';
import liveEvents from '@/assets/icons/vn-icon-app-events.svg';
import assetLibrary from '@/assets/icons/vn-icon-asset-library.svg';
import transitionPackages from '@/assets/icons/vn-icon-automated-transitions.svg';
import billing from '@/assets/icons/vn-icon-billing.svg';
import channelManager from '@/assets/icons/vn-icon-channel-manager.svg';
import cmsUser from '@/assets/icons/vn-icon-cms-users.svg';
import content from '@/assets/icons/vn-icon-content.svg';
import dashboards from '@/assets/icons/vn-icon-dashboards.svg';
import destinations from '@/assets/icons/vn-icon-destinations.svg';
import globalInterstitials from '@/assets/icons/vn-icon-global-interstitials.svg';
import health from '@/assets/icons/vn-icon-health.svg';
import inputs from '@/assets/icons/vn-icon-inputs-scheduler.svg';
import live from '@/assets/icons/vn-icon-live.svg';
import liveGuideIcon from '@/assets/icons/vn-icon-live-guide.svg';
import liveGuide from '@/assets/icons/vn-icon-live-guide.svg';
import liveProgram from '@/assets/icons/vn-icon-live-programs.svg';
import marketing from '@/assets/icons/vn-icon-marketing.svg';
import monetization from '@/assets/icons/vn-icon-monetization.svg';
import monitoringDashboard from '@/assets/icons/vn-icon-monitoring.svg';
import movies from '@/assets/icons/vn-icon-movies.svg';
import mrssFeed from '@/assets/icons/vn-icon-mrss-feed-config.svg';
import mrssMapper from '@/assets/icons/vn-icon-mrss-mapper.svg';
import navigation from '@/assets/icons/vn-icon-ou-navigation.svg';
import products from '@/assets/icons/vn-icon-products.svg';
import pushNotifications from '@/assets/icons/vn-icon-push-notifications.svg';
import series from '@/assets/icons/vn-icon-series.svg';
import settings from '@/assets/icons/vn-icon-settings.svg';
import siteSettings from '@/assets/icons/vn-icon-site-settings.svg';
import uploadAsset from '@/assets/icons/vn-icon-upload-assets.svg';
import usage from '@/assets/icons/vn-icon-usage.svg';
import workflowConfig from '@/assets/icons/vn-icon-workflow-config.svg';
import type { OU } from '@/models/Ou';
import type { Permissions } from '@/models/Permissions';

import { Permission } from '../../../enums';

export const SidebarNavIcon = ({ src, alt }: { src: string; alt: string }) => (
  <span
    className="sidebar-nav-icon"
    role="img"
    aria-label={alt}
    style={{ ['--sidebar-nav-icon' as string]: `url(${src})` }}
  />
);

const notificationAdminIcon = <SettingsOutlined sx={{ fontSize: 18, color: 'inherit' }} />;

type SlidebarItem = {
  id: string;
  text: string;
  path: string;
  permission: Permission;
  icon: JSX.Element;
  children: SlidebarItem[];
  featureFlagEnable: boolean;
  location?: 'standard' | 'bottom';
  onClick?: (e: MouseEvent) => void;
};

const getSideBarList = (ouData: OU, permissions: Permissions, flags: LDFlagSet) => {
  return [
    {
      id: 'dashboards',
      text: 'Dashboards',
      path: '',
      icon: <SidebarNavIcon src={dashboards} alt="Dashboards" />,
      children: [
        {
          id: 'analytics',
          text: 'Analytics',
          path: '/dashboards/analytics',
          permission: Permission.AnalyticsDashboard,
          icon: <SidebarNavIcon src={analytics} alt="Analytics" />,
        },
        {
          id: 'monitoringDashboard',
          text: 'Monitoring',
          path: '/dashboards/stream-monitoring',
          permission: Permission.Dashboard,
          icon: <SidebarNavIcon src={monitoringDashboard} alt="Monitoring" />,
        },
        {
          id: 'health',
          text: 'Health',
          path: '/dashboards/health',
          permission: Permission.OUSuperAdmin,
          icon: <SidebarNavIcon src={health} alt="Health" />,
        },
        {
          id: 'usage',
          text: 'Usage',
          path: '/dashboards/usage',
          permission: Permission.OUSuperAdmin,
          icon: <SidebarNavIcon src={usage} alt="Usage" />,
        },
        {
          id: 'destinations',
          text: 'Destinations',
          path: '/dashboards/destinations',
          permission: Permission.OUSuperAdmin,
          icon: <SidebarNavIcon src={destinations} alt="Destinations" />,
        },
        {
          id: 'app-analytics',
          text: 'App Analytics',
          path: '/dashboards/app-analytics',
          permission: Permission.AnalyticsDashboard,
          icon: <SidebarNavIcon src={analytics} alt="Analytics" />,
        },
        {
          id: 'streaming-analytics',
          text: 'Streaming Analytics',
          path: '/dashboards/streaming-analytics/overview',
          icon: <SidebarNavIcon src={analytics} alt="Streaming Analytics" />,
        },
      ],
    },
    {
      id: 'uploadAssets',
      text: 'Upload Assets',
      path: '/assets/new',
      permission: Permission.Ingest,
      icon: <SidebarNavIcon src={uploadAsset} alt="Upload Assets" />,
    },
    {
      id: 'assetLibrary',
      text: 'Asset Library',
      path: '/assets/list',
      permission: Permission.ListSearchAssets,
      icon: <SidebarNavIcon src={assetLibrary} alt="Asset Library" />,
    },
    {
      id: 'channelManager',
      text: 'Channel Manager',
      path: '/channelmanager/list',
      permission: Permission.ChannelManager,
      icon: <SidebarNavIcon src={channelManager} alt="Channel Manager" />,
      children: [],
    },
    {
      id: 'live',
      text: 'Live',
      icon: <SidebarNavIcon src={live} alt="Live" />,
      children: [
         {
          id: 'liveBroadcasts',
          text: 'Live Broadcasts',
          path: '/live/broadcasts/list',
          icon: <SidebarNavIcon src={liveProgram} alt="Live Broadcasts" />,
        },
        {
          id: 'inputSources',
          text: 'Input Sources',
          path: '/live/input-sources/list',
          icon: <i className="fa fa-solid fa-sliders" aria-hidden="true" />,
        },
        {
          id: 'livePrograms',
          text: 'RTMP Setup (Legacy)',
          path: '/live/programs/list',
          icon: <SidebarNavIcon src={liveProgram} alt="RTMP Setup (Legacy)" />,
        },
        // {
        //   id: 'inputs',
        //   text: 'Inputs Scheduler (Legacy)',
        //   path: '/live/inputs/list',
        //   permission: Permission.OUSuperAdmin,
        //   icon: <SidebarNavIcon src={inputs} alt="Inputs Scheduler (Legacy)" />,
        // },
        {
          id: 'appEvents',
          text: 'App Events',
          path: '/live/events/list',
          permission: Permission.ListSearchAppEvents,
          icon: <SidebarNavIcon src={liveEvents} alt="App Events" />,
        },
        {
          id: 'liveGuide',
          text: 'Live Guide',
          path: '/live/guide/list',
          permission: Permission.ListSearchAppEvents,
          icon: <SidebarNavIcon src={liveGuideIcon} alt="Live Guide" />,
        },
      ],
    },
    {
      id: 'content',
      text: 'Content',
      icon: <SidebarNavIcon src={content} alt="Content" />,
      permission: Permission.Ingest,
      children: [
        {
          id: 'movies',
          text: 'Movies',
          path: '/movies/list',
          icon: <SidebarNavIcon src={movies} alt="Movies" />,
          children: [],
        },
        {
          id: 'series',
          text: 'Series',
          path: '/series/list',
          icon: <SidebarNavIcon src={series} alt="Series" />,
        },
      ],
    },
    {
      id: 'epg',
      text: 'EPG',
      path: '',
      icon: <SidebarNavIcon src={liveGuide} alt="EPG" />,
    },
    {
      id: 'monetization-dropdown',
      text: 'Monetization',
      icon: <SidebarNavIcon src={monetization} alt="Monetization" />,
      children: [
        {
          id: 'monetization',
          text: 'Ad Tags Management',
          path: '/ads',
          icon: <SidebarNavIcon src={adTag} alt="Ad Tags Management" />,
          permission: Permission.AdManagement,
          featureFlagEnable: flags['fuel-ui-show-self-service-ads'],
        },
        {
          id: 'products',
          text: 'Products',
          path: '/monetization/products/list',
          permission: Permission.ListSearchProducts,
          icon: <SidebarNavIcon src={products} alt="Products" />,
        },
        {
          id: 'productCoupons',
          text: 'Coupons',
          path: '/monetization/coupons/list',
          permission: Permission.ListSearchProducts,
          icon: <SidebarNavIcon src={products} alt="Coupons" />,
        },
      ],
    },
    {
      id: 'marketing',
      text: 'Marketing',
      icon: <SidebarNavIcon src={marketing} alt="Marketing" />,
      children: [
        {
          id: 'pushNotifications',
          text: 'Push Notifications',
          path: '/marketing/push-notifcations',
          permission: Permission.ListSearchPushNotifications,
          icon: <SidebarNavIcon src={pushNotifications} alt="Push Notifications" />,
        },
        // {
        //   id: 'mailchimp',
        //   text: 'Mailchimp',
        //   path: '',
        //   permission: Permission.ListSearchMailchimp,
        //   icon: <SidebarNavIcon src={mailchimp} alt="Mailchimp" />,
        // },
        // {
        //   id: 'firebase',
        //   text: 'Firebase',
        //   path: '',
        //   permission: Permission.ListSearchFirebase,
        //   icon: <SidebarNavIcon src={firebase} alt="Firebase" />,
        // },
      ],
    },
    {
      id: 'appUsers',
      text: 'App Users',
      path: '/app-users',
      icon: <SidebarNavIcon src={account} alt="App Users" />,
    },
    {
      id: 'settings',
      text: 'Settings',
      path: '',
      icon: <SidebarNavIcon src={settings} alt="Settings" />,
      children: [
        {
          id: 'settings-admin',
          text: 'Admin (Legacy)',
          path: '/legacy-admin',
          permission: Permission.OUSuperAdmin,
          icon: <SidebarNavIcon src={adminLegacy} alt="Admin Legacy" />,
        },
        {
          id: 'admin',
          text: 'Admin',
          path: '/settings/admin',
          permission: Permission.OUAdmin,
          icon: <SidebarNavIcon src={admin} alt="Admin" />,
        },
        {
          id: 'advancedAdmin',
          text: 'Advanced Admin',
          path: '/settings/advanced-admin',
          permission: Permission.OUSuperAdmin,
          icon: <SidebarNavIcon src={advancedAdmin} alt="Advanced Admin" />,
        },
        {
          id: 'ouNavigation',
          text: 'Navigation',
          path: '/settings/ou-navigation',
          permission: Permission.OUSuperAdmin,
          icon: <SidebarNavIcon src={navigation} alt="Navigation" />,
        },
        {
          id: 'mrssFeedConfig',
          text: 'MRSS Feed Config',
          path: '/settings/mrss-feed-config',
          permission: Permission.OUSuperAdmin,
          icon: <SidebarNavIcon src={mrssFeed} alt="Mrss Feed" />,
        },
        {
          id: 'mrssMapper',
          text: 'MRSS Mapper',
          path: '/settings/mrss-mapper',
          permission: Permission.OUSuperAdmin,
          icon: <SidebarNavIcon src={mrssMapper} alt="mrssMapper" />,
          children: [],
        },
        {
          id: 'globalInterstitials',
          text: 'Global Interstitials',
          path: `/channelmanager/editor/${ouData?.ouId}`,
          icon: <SidebarNavIcon src={globalInterstitials} alt="globalInterstitials" />,
          children: [],
        },
        {
          id: 'transitionPackages',
          text: 'Automated Transitions',
          path: '/transitiontemplates/list',
          permission: Permission.ListSearchTransitionTemplates,
          icon: <SidebarNavIcon src={transitionPackages} alt="transitionPackages" />,
        },
        {
          id: 'eventWorkflows',
          text: 'Event Workflows',
          path: '/event-workflows',
          permission: Permission.EventWorkflow,
          icon: <SidebarNavIcon src={workflowConfig} alt="workflowConfig" />,
        },
      ],
    },
    {
      id: 'site',
      text: 'App Settings',
      path: '/site/layout',
      permission: Permission.OUAdmin,
      icon: <SidebarNavIcon src={siteSettings} alt="App Settings" />,
      // featureFlagEnable: ouData.metaData.enableSite,
    },
    {
      id: 'workflowConfig',
      text: 'Workflow Config',
      path: '/workflow-config',
      icon: <SidebarNavIcon src={workflowConfig} alt="Work flow Config" />,
      permission: Permission.OUSuperAdmin,
    },
    {
      id: 'account-dropdown',
      text: 'Account',
      icon: <SidebarNavIcon src={account} alt="Account" />,
      path: '',
      permission: Permission.OUAdmin,
      children: [
        // {
        //   id: 'profile',
        //   text: 'Profile',
        //   path: '',
        //   icon: <span className="no-icon" />,
        // },
        {
          id: 'account',
          text: 'CMS Users',
          path: '/account/cms-user-management',
          icon: <SidebarNavIcon src={cmsUser} alt="CMS Users" />,
        },

        {
          id: 'billing',
          text: 'Billing',
          path: '/account/billing',
          icon: <SidebarNavIcon src={billing} alt="Billing" />,
        },
        {
          id: 'accountActivity',
          text: 'Account Activity',
          path: '/account/audit-trail',
          icon: <SidebarNavIcon src={accountActivity} alt="Account Activity" />,
        },
      ],
    },
    permissions?.has(Permission.OUSuperAdmin) && {
      id: 'vnms',
      text: 'Notification Admin',
      path: '/notifications/admin',
      location: 'bottom',
      permission: Permission.OUSuperAdmin,
      icon: notificationAdminIcon,
      children: [],
    },
  ].filter(Boolean) as SlidebarItem[];
};

export default getSideBarList;
